import './Networking.css'
import NetworkingOpt from "../../components/seo/NetworkingOpt";

export default function NetworkConnectivity() {
    return (
        <>
            <NetworkingOpt
                title="Network Connectivity"
                description="Explore network connectivity topics including static routes, dynamic routing, IGPs, BGP, RIP, EIGRP, OSPF, FHRP, and HSRP."
                keywords="network connectivity, routing, static routes, dynamic routing, bgp, rip, eigrp, ospf, hsrp"
            />

            <article className='content'>
                <h1 style={{display:'flex', justifyContent:'center' }}>Network Connectivity</h1>

                <h2>Introduction</h2>
                
                <p>Network Route: Route to a network/subnet (mask length &lt; /32).</p>
                <p>Host Route: Route to a specific host (/32).</p>
                <p>Routers form adjacencies (neighbour relationships) and use dynamic routing protocols to share route information.</p>
                <p>When multiple routes exist to a destination, routers choose the best route using the metric value.</p>
                <p>Lower metric = better route.</p>
                
                
                <h2>Types of Routing Protocols</h2>
                
                <h3>Interior Gateway Protocol (IGP) Algorithms</h3>

                <p>Used within a single autonomous system (AS) — e.g., within a company.</p>

                <h4><em>Distance Vector</em></h4>

                <p>Examples: RIP, EIGRP</p>
                <p>Invented before link-state protocols.</p>
                <p>Operates by sending known destination networks and their metrics — “routing by rumor”.</p>
                <p>Routers only know about networks beyond their neighbours.</p>
                <p>Called “distance vector” because routers learn:</p>
                <p>Distance: Metric value</p>
                <p>Vector: Direction (next-hop router)</p>
                
                <h4><em>Link State</em></h4>

                <p>Examples: OSPF, IS-IS</p>
                <p>Each router builds a connectivity map of the network.</p>
                <p>Routers advertise their connected networks to neighbours; these are flooded until all routers share the same map.</p>
                <p>Uses more CPU resources but reacts faster to network changes.</p>

                <h3>Exterior Gateway Protocol (EGP) Algorithms</h3>

                <p>Used between autonomous systems, e.g., BGP (Border Gateway Protocol).</p>


                <h2>Dynamic Routing Protocol Metrics</h2>

                <p>The routing table stores the best routes to each destination.</p>
                <p>Metric determines the best path — each protocol uses a different metric type.</p>
                <p>If multiple equal-cost routes exist, the router performs ECMP (Equal Cost Multi-Path) load balancing.</p>
                <table>
                    <tr><td>Protocol</td><td>Metric</td><td>Description</td></tr>
                    <tr><td>RIP</td><td>Hop Count</td><td>Each router = 1 hop; bandwidth ignored</td></tr>
                    <tr><td>EIGRP</td><td>Bandwidth &amp; Delay</td><td>Uses slowest link bandwidth + total delay</td></tr>
                    <tr><td>OSPF</td><td>Cost</td><td>Based on bandwidth; total cost of all links</td></tr>
                    <tr><td>IS-IS</td><td>Cost</td><td>Default cost = 10 per link; manual adjustment required</td></tr>
                </table>
                
                
                <h2>Administrative Distance (AD)</h2>

                <p>Used when multiple routing protocols exist.</p>
                <p>Metric compares routes within the same protocol.</p>
                <p>AD compares routes between different protocols.</p>

                <h3>Floating Static Routes:</h3>

                <p>Static route with increased AD → less preferred.</p>
                <p>Becomes active only if the dynamic route is lost.</p>

                <h2>Routing Information Protocol (RIP)</h2>
                
                <h3>Configuration:</h3>
                
                <pre>
                    <code>R1(config-router)# network 10.0.0.0</code><br />
                    <code>R1(config-router)# passive-interface g2/0</code>
                </pre>
                
                <p>network command activates RIP on matching interfaces.</p>
                <p>passive-interface stops sending RIP updates on specified interfaces.</p>
                <p>The same command applies to EIGRP and OSPF.</p>
                
                <h3>Overview:</h3>

                <p>Metric: Hop Count (bandwidth irrelevant).</p>
                <p>Message Types:</p>
                <p>Request: Ask neighbours for a routing table.</p>
                <p>Response: Send local routing table.</p>
                <p>RIP routers share routing tables every 30 seconds.</p>
                <p>Broadcast vs Multicast:</p>
                <p>Broadcast → all devices</p>
                <p>Multicast → specific group members</p>

                <h3>Versions:</h3>
                
                <table>
                    <tr><td>Version</td><td>IP Support</td><td>Features</td><td>Address Type</td></tr>
                    <tr><td>RIPv1</td><td>IPv4</td><td>Classful, no VLSM/CIDR</td><td>Broadcast 255.255.255.255</td></tr>
                    <tr><td>RIPv2</td><td>IPv4</td><td>Supports VLSM/CIDR</td><td>Multicast 224.0.0.9</td></tr>
                    <tr><td>RIPng</td><td>IPv6</td><td>Next Generation</td><td>—</td></tr>
                </table>
                
                
                
                
                <h2>Enhanced Interior Gateway Routing Protocol (EIGRP)</h2>

                <h3>Configuration:</h3>
                
                <pre>
                    <code>R1(config)# router eigrp &lt;AS-number&gt;</code><br />
                    <code>R1(config-router)# network 10.0.0.0 0.0.0.255</code><br />
                    <code>R1(config-router)# no auto-summary</code>
                </pre>
                
                <p>AS number must match between routers to form adjacency.</p>
                <p>Auto-summary may need disabling depending on the IOS version.</p>
                <p>Uses wildcard masks (inverse of subnet masks).</p>
                <p>Example: Subnet mask 255.255.255.00.0.0.255.</p>

                <h4>Router ID priority:</h4>
                <p>Manual configuration</p>
                <p>Highest IP on loopback interface</p>
                <p>Highest IP on physical interface</p>

                <h3>Overview</h3>

                <p>Cisco proprietary (now open standard).</p>
                <p>Advanced / hybrid distance vector protocol.</p>
                <p>Faster convergence than RIP.</p>
                <p>No 15-hop limit.</p>
                <p>Uses multicast address 224.0.0.10.</p>
                <p>Supports unequal-cost load balancing (unique among IGPs).</p>
                <p>Default ECMP load balancing over 4 paths.</p>
                

                <h2>OSPF</h2>
                
                <h3>Basic Cisco IOS OSPF Configuration</h3>

                <pre>
                    <code>R1(config)# router ospf 1</code><br />
                    <code>R1(config-router)# router-id 1.1.1.1</code><br />
                    <code>R1(config-router)# network 192.168.12.0 0.0.0.255 area 0</code><br />
                    <code>R1(config-router)# network 10.1.1.0 0.0.0.255 area 1</code>
                </pre>
                
                <p>router ospf 1Starts OSPF process ID 1</p>
                <p>router-id 1.1.1.1 Manually sets router ID (recommended)</p>
                <p>network X Y area Z Advertises networks and activates OSPF on matching interfaces</p>
                
                <h3>OSPF Authentication (MD5)</h3>

                <pre>
                    <code>R1(config)# interface g0/0</code><br />
                    <code>R1(config-if)# ip ospf message-digest-key 1 md5 MYKEY</code><br />
                    <code>R1(config-if)# ip ospf authentication message-digest</code>
                </pre>
                
                <h4><em>Purpose:</em></h4>
                <p>Secures OSPF communication using MD5 authentication.</p>
                
                <h3>OSPFv3 (IPv6) </h3>
                
                <pre>
                    <code>R1(config)# ipv6 router ospf 10</code><br />
                    <code>R1(config-rtr)# router-id 1.1.1.1</code><br />
                    <code>R1(config)# interface g0/0</code><br />
                    <code>R1(config-if)# ipv6 ospf 10 area 0</code>
                </pre>
                
                <h4><em>Purpose: </em></h4>
                <p>Enables OSPFv3 for IPv6 networks. </p>
                
                <h3>OSPF Overview</h3>

                <p>Link-State Routing Protocol using Dijkstra’s SPF algorithm.</p>
                <h4>Versions:</h4>
                <p>OSPFv1 (1989) → obsolete</p>
                <p>OSPFv2 (1998) → IPv4</p>
                <p>OSPFv3 (2008) → IPv6/IPv4</p>
                
                
                <h3>Link State Advertisement (LSA)</h3>

                <p>Routers store network info in LSAs, organized in the Link State Database (LSDB).</p>
                <p>LSAs are flooded until all routers share identical LSDBs.</p>
                <p>Each LSA has an aging timer (30 min) — refreshed upon expiry.</p>
                
                
                <h3>OSPF Process</h3>

                <p>Form neighbour relationships.</p>
                <p>Exchange LSAs.</p>
                <p>Calculate best routes and update routing tables.</p>
                
                
                <h3>OSPF Areas</h3>

                <p>Divide networks to improve scalability and performance.</p>
                <p>Backbone Area (Area 0) → must connect all other areas.</p>

                <h4>Router Types:</h4>
                <p>Internal Router → all interfaces in one area.</p>
                <p>ABR (Area Border Router) → connects multiple areas, maintains separate LSDBs.</p>
                <p>ASBR (Autonomous System Boundary Router) → connects to external networks.</p>

                <h4>Route Types:</h4>
                <p>Intra-area → within the same area.</p>
                <p>Inter-area → between areas.</p>
                
                <h3>OSPF Metric (Cost)</h3>

                <p>Cost = Reference Bandwidth / Interface Bandwidth</p>
                <p>Default reference bandwidth = 100 Mbps</p>
                <p>Example: 100/10=10</p>
                <p>Minimum cost = 1</p>

                <h4><em>Modify Cost:</em></h4>
                <p>Change reference bandwidth:</p>
                <pre>router ospf &lt;process-id&gt;</pre>  
                <pre>auto-cost reference-bandwidth &lt;mbps&gt;</pre>
                <p>Manual cost:</p>
                <pre>ip ospf cost &lt;value&gt;</pre>
                <p>Change interface bandwidth:</p>
                <pre>bandwidth &lt;kbps&gt;</pre>

                <h3>Useful Show Commands</h3>

                <pre>
                    <code>show ip ospf neighbor → View neighbour relationships</code><br />
                    <code>show ip interface f2/0 → Check OSPF interface details</code>
                </pre>

                <h3>Passive Interface</h3>

                <p>Make all interfaces passive: <pre>passive-interface default</pre></p>
                <p>Activate specific ones: <pre>no passive-interface &lt;interface-id&gt;</pre></p>

                <h3>Loopback Interface</h3>

                <p>Always up/up</p>
                <p>Cost = 1</p>
                <p>Often used for router ID assignment.</p>

                <h3>OSPF LSA Types</h3>

                <table>
                    <tr><td>Type</td><td>Name</td><td>Description</td></tr>
                    <tr><td>1</td><td>Router LSA</td><td>Generated by all routers; lists connected networks</td></tr>
                    <tr><td>2</td><td>Network LSA</td><td>Generated by DR; lists routers on multi-access network</td></tr>
                    <tr><td>5</td><td>AS-External LSA</td><td>Generated by ASBR; describes external routes</td></tr>
                </table>

                <h3>OSPF Neighbour States:</h3>
                <table>
                    <tr><td>State</td><td>Description</td></tr>
                    <tr><td>Down</td><td>Sends Hello to 224.0.0.5</td></tr>
                    <tr><td>Init</td><td>Receives Hello (no RID yet)</td></tr>
                    <tr><td>2-Way</td><td>Mutual Hello exchange; adjacency formed</td></tr>
                    <tr><td>Exstart</td><td>Master/slave negotiation; DBD exchange begins</td></tr>
                    <tr><td>Exchange</td><td>DBD packets exchanged</td></tr>
                    <tr><td>Loading</td><td>Requests missing LSAs (LSR/LSU/LSAck)</td></tr>
                    <tr><td>Full</td><td>LSDB synchronized; adjacency complete</td></tr>
                </table>
                
                <h4><em>Timers:</em></h4>
                <p>Hello: 10s (Ethernet)</p>
                <p>Dead: 40s</p>
                
                <h4>Neighbour Requirements</h4>
                <p>Same area number</p>
                <p>Same subnet</p>
                <p>Unique router IDs</p>
                <p>Matching Hello/Dead timers</p>
                <p>Matching authentication</p>
                <p>Matching MTU and network type</p>

                <h3>OSPF Network Types</h3>

                <h4><em>1. Broadcast</em></h4>
                <p>Default on Ethernet/FDDI</p>
                <p>Uses multicast 224.0.0.5</p>
                <p>DR/BDR election based on:</p>
                <p>Highest interface priority</p>
                <p>Highest router ID</p>
                <p>DR/BDR are non-preemptive</p>
                <p>Communication:</p>
                <p>DR/BDR → 224.0.0.6</p>
                <p>Others → 224.0.0.5</p>

                <h4><em>2. Point-to-Point</em></h4>
                <p>Default on serial (PPP/HDLC)</p>
                <p>No DR/BDR election</p>
                <p>Uses multicast 224.0.0.5</p>
                <p>DCE side sets clock rate</p> 
                <pre>
                    <code>R1(config-if)# encapsulation ppp</code><br />
                    <code>R1# show controllers &lt;interface&gt;</code><br />
                    <code>R1(config-if)# clock rate &lt;bps&gt;</code>
                </pre>
                
                
                <h2>First Hop Redundancy Protocol</h2>
                
                <p>Protects the default gateway on a subnet by providing redundancy through multiple routers.</p>
                <p>If the active router fails, a backup router takes over the gateway address within seconds.</p>
                <p>Configures a virtual IP between routers and hosts — assigned as the default	gateway.</p>
              
                <p>Gratuitous ARP:</p>
                <p>ARP replies sent without being requested.</p>
                <p>Frames are broadcast to ffff.ffff.ffff.ffff.</p>
                <p>Normal ARP replies are unicast.</p>
               
                <p>Non-preemptive:</p>
                <p>The active router does not relinquish its role automatically when the former active router returns.</p>
                
                
                <h2>Hot Standby Router Protocol(HSRP) </h2>

                <p>Cisco proprietary protocol.</p>
                <p>Elects an active and standby router.</p>
 
                <h3>Versions:</h3>
                <p>HSRPv1: IPv4 only</p>
                <p>HSRPv2: Adds IPv6 support and increases group capacity</p>
           
                <h3>Multicast IPv4 Addresses:</h3>

                <p>v1 → 224.0.0.2</p>
                <p>v2 → 224.0.0.102</p>
      
                <h3>Virtual MAC Addresses:</h3>

                <p>v1 → 0000.0c07.acxx (xx = group number)</p>
                <p>v2 → 0000.0c9f.fxxx (xxx = group number)</p>

                <p>Load Balancing:</p>
                <p>In multi-subnet/VLAN setups, different routers can act as active gateways for different groups.</p>
                
                <h3><em>HSRP Configuration Example: </em></h3>
                <pre>
                    <code>R1(config)# interface g0/0</code><br />
                    <code>R1(config-if)# standby version 2</code><br />
                    <code>R1(config-if)# standby 1 ip &lt;virtual-IP&gt;</code><br />
                    <code>R1(config-if)# standby 1 priority &lt;value&gt;</code><br />
                    <code>R1(config-if)# standby 1 preempt</code><br />
                    <code>R1# show standby</code>
                </pre>
                
                               
                <h2>Virtual Router Redundancy Protocol (VRRP): </h2>

                <p>Open standard</p>
                <p>A master and backup router is elected.</p>
                <p>Multicast IPv4 address: 224.0.0.18</p>
                <p>Virtual Mac address: 0000.5e00.01xx (xx = VRRP group number)</p>
                
                <h2>Gateway Load Balancing Protocol (GLBP): </h2>
                <p>Cisco proprietary protocol.</p>
                <p>Provides load balancing among multiple routers in a single subnet.</p>
                
                <h3><em>Roles:</em></h3>
                <p>AVG (Active Virtual Gateway): Elected to assign AVFs.</p>
                <p>AVF (Active Virtual Forwarder): Up to four per group; each acts as a default gateway for a subset of hosts.</p>
                
                <p>Multicast IPv4 Address: 224.0.0.102</p>
                <p>Virtual MAC Address: 0007.6400.xxxy</p>
                <p>xx = GLBP group number</p>
                <p>xy = AVF number</p>
            </article>

            </>
            )
    }
;